/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useMemo, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View, useWindowDimensions } from 'react-native';
import { Modal } from '../../shared/modals/Modal';
import { ModalFormScroll } from '../../shared/modals/ModalFormScroll';
import { ModalScreenHeader } from '../../shared/modals/ModalScreenHeader';
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { modalShellShared } from '../../shared/modals/modalShellShared.styles';
import { CONTENT_WIDE_BREAKPOINT } from '../../shared/layout/breakpoints';
import { useModalBackdropPadding } from '../../shared/ui/hooks-ui/useModalBackdropPadding';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { spacing } from '../../shared/theme/public-api';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { ExpenseFiscalSection } from '../../expenses/components/ExpenseFiscalSection';
import {
  emptyExpenseFiscalDraft,
  expenseFiscalDraftIsEmpty,
  expenseFiscalDraftToInput,
  expenseFiscalDraftTotal,
  expenseFiscalRowToDraft,
  validateExpenseFiscalInvoice,
  type ExpenseFiscalDraft,
} from '../../expenses/domain-api';
import type { ExpenseRow } from '../../expenses/domain-api';
import { showError } from '../../shared/ui/appToast';

type Props = {
  visible: boolean;
  expense: ExpenseRow | null;
  branchId: string | null;
  /** Monto del gasto para warning de desfasaje (bruto recepción). */
  expenseAmount: number | null;
  title: string;
  subtitle?: string;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (fiscal: NonNullable<ReturnType<typeof expenseFiscalDraftToInput>>) => void;
  /** Draft inicial (NC post-devolución); si no, se deriva del expense. */
  initialDraft?: ExpenseFiscalDraft | null;
};

export function PurchaseExpenseFiscalModal({
  visible,
  expense,
  branchId,
  expenseAmount,
  title,
  subtitle,
  submitting,
  onClose,
  onSubmit,
  initialDraft,
}: Props) {
  const { width } = useWindowDimensions();
  const isMobile = width < CONTENT_WIDE_BREAKPOINT;
  const backdropPadding = useModalBackdropPadding();
  const [draft, setDraft] = useState<ExpenseFiscalDraft>(emptyExpenseFiscalDraft());
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) {
      setDraft(emptyExpenseFiscalDraft());
      setLocalError(null);
      return;
    }
    if (initialDraft) {
      setDraft(initialDraft);
      return;
    }
    if (expense) {
      setDraft(expenseFiscalRowToDraft(expense));
    }
  }, [visible, expense?.id, initialDraft]);

  const fiscalTotal = useMemo(() => expenseFiscalDraftTotal(draft), [draft]);
  const amountMismatch =
    expenseAmount != null &&
    fiscalTotal > 0 &&
    Math.abs(fiscalTotal - expenseAmount) > 0.5;

  const handleSave = () => {
    if (expenseFiscalDraftIsEmpty(draft)) {
      setLocalError('Completá el comprobante o cancelá.');
      return;
    }
    const fiscal = expenseFiscalDraftToInput(draft);
    if (!fiscal) {
      setLocalError('Completá el comprobante fiscal.');
      return;
    }
    const validation = validateExpenseFiscalInvoice(fiscal);
    if (validation) {
      setLocalError(validation);
      return;
    }
    setLocalError(null);
    onSubmit(fiscal);
  };

  const requestClose = () => {
    if (submitting) return;
    onClose();
  };

  if (!visible) return null;

  const body = (
    <>
      <ExpenseFiscalSection
        draft={draft}
        onChange={setDraft}
        expenseAmount={expenseAmount}
        submitting={submitting}
        branchId={branchId}
        testIDPrefix="purchases.fiscal"
      />
      {amountMismatch ? (
        <Text style={styles.warn}>
          El total del comprobante ({fiscalTotal.toFixed(2)}) no coincide con el gasto (
          {expenseAmount?.toFixed(2)}). Podés guardar igual; revisá neto e IVA.
        </Text>
      ) : null}
      {localError ? <Text style={styles.error}>{localError}</Text> : null}
    </>
  );

  const footer = (
    <TouchableOpacity
      style={[styles.primaryBtn, submitting && styles.btnDisabled]}
      onPress={() => {
        try {
          handleSave();
        } catch (err) {
          showError('Error', err instanceof Error ? err.message : 'No pude guardar.');
        }
      }}
      disabled={submitting}
      accessibilityRole="button"
      testID="purchases.fiscal.save"
    >
      <Text style={styles.primaryBtnText}>{submitting ? 'Guardando…' : 'Guardar comprobante'}</Text>
    </TouchableOpacity>
  );

  return (
    <Modal
      visible={visible}
      animationType="fade"
      transparent
      onRequestClose={requestClose}
      testID="purchases.fiscal.modal"
    >
      <View style={[posUi.modalBackdrop, modalExperience.modalRoot, backdropPadding]}>
        <View
          style={[
            posUi.modalCard,
            modalShellShared.modalCardShell,
            modalExperience.modalWidthStandard,
            { gap: spacing.sm, width: '100%', alignSelf: 'center', maxHeight: isMobile ? '96%' : '92%' },
          ]}
        >
          <ModalScreenHeader title={title} subtitle={subtitle} onClose={requestClose} />
          <ModalFormScroll contentContainerStyle={modalExperience.formPanel}>{body}</ModalFormScroll>
          <View style={[posUi.modalActions, modalExperience.footerActions]}>{footer}</View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  warn: {
    marginTop: 12,
    fontFamily: ticketFonts.sans,
    fontSize: 13,
    color: ticketColors.warn,
  },
  error: {
    marginTop: 12,
    fontFamily: ticketFonts.sans,
    fontSize: 13,
    color: ticketColors.bad,
  },
  primaryBtn: {
    flex: 1,
    backgroundColor: ticketColors.gold,
    borderRadius: 10,
    paddingVertical: 14,
    alignItems: 'center',
  },
  primaryBtnText: {
    fontFamily: ticketFonts.sans,
    fontSize: 16,
    fontWeight: '600',
    color: ticketColors.goldInk,
  },
  btnDisabled: {
    opacity: 0.6,
  },
});
